import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as Yup from "yup";

import { useAlertContext } from "../../../../../alert/AlertContext";
import { HANDLE_ERROR_LOCALLY_HEADER } from "../../../../../api/handleErrorLocally";
import { useFormikWithDirtyForm } from "../../../../../forms/useFormikWithDirtyForm";
import { getUploadErrorMessage } from "../../../../../upload/uploadNotifications";
import { nameof } from "../../../../../utils/nameof";
import { AssetIncomeTab, AssetInventoryCompletenessSection } from "../../../../api/ClientFileApi";
import { useAssetInventoryApi, useOptionalAssetInventoryDocumentsApi } from "../../../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../../../query/clientFileQueryKeys";
import { useAssetDocumentMoves } from "../../../hooks/useAssetDocumentMoves";
import { useMissingFieldMessages } from "../../../hooks/useMissingFieldMessages";
import { usePendingMovesField } from "../../../hooks/usePendingMovesField";
import { describeMissingIncomeField } from "../IncomeView.missingFields";
import { AssetIncomeFormValues } from "../IncomeView.types";
import { fromIncomeResponse, toIncomeRequest } from "../IncomeView.utils";
import { createIncomeEntrySchema, createSelfEmploymentSchema } from "../IncomeView.validation";
import { useIncomeTypes } from "./useIncomeTypes";

function toInitialValues(data: AssetIncomeTab | undefined): AssetIncomeFormValues {
  return {
    hasSelfEmploymentIncome: data?.selfEmployment.hasSelfEmploymentIncome ?? null,
    selfEmploymentDescription: data?.selfEmployment.description ?? null,
    selfEmploymentMonthlyIncome: data?.selfEmployment.monthlyIncome ?? null,
    incomes: (data?.incomes ?? []).map(fromIncomeResponse),
    incomeProofs: [],
    documentMoves: [],
  };
}

export function useAssetIncome(clientId: string) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();
  const clientsApi = useAssetInventoryApi();
  const documentsApi = useOptionalAssetInventoryDocumentsApi();
  const moveDocuments = useAssetDocumentMoves(clientId);
  const { traitsOf } = useIncomeTypes();

  const queryKey = [ClientFileQueryKey.AssetInventoryIncome, clientId];

  const { data } = useQuery({
    queryKey,
    queryFn: () => clientsApi.getAssetInventoryIncome({ clientId }),
    enabled: !!clientId,
  });

  const uploadIncomeProofs = async (files: File[]): Promise<File[]> => {
    if (!documentsApi) {
      return files;
    }

    const failed: File[] = [];
    await Promise.all(
      files.map(async (file) => {
        try {
          const formData = new FormData();
          formData.append("file", file);
          await documentsApi.uploadAssetInventoryIncomeProof(
            { clientId },
            { body: formData, headers: { [HANDLE_ERROR_LOCALLY_HEADER]: "true" } }
          );
        } catch (error: unknown) {
          failed.push(file);
          showError(await getUploadErrorMessage(error, file.name, t));
        }
      })
    );

    void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryIncomeProofs, clientId] });
    return failed;
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        ...createSelfEmploymentSchema(t),
        incomes: Yup.array().of(createIncomeEntrySchema(t, traitsOf) as unknown as Yup.AnyObjectSchema),
      }),
    [t, traitsOf]
  );

  const initialValues = useMemo(() => toInitialValues(data), [data]);

  const formik = useFormikWithDirtyForm<AssetIncomeFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values, helpers) => {
      const selfEmploymentChanged =
        values.hasSelfEmploymentIncome !== initialValues.hasSelfEmploymentIncome ||
        values.selfEmploymentDescription !== initialValues.selfEmploymentDescription ||
        values.selfEmploymentMonthlyIncome !== initialValues.selfEmploymentMonthlyIncome;
      const incomesChanged = JSON.stringify(values.incomes) !== JSON.stringify(initialValues.incomes);

      if (selfEmploymentChanged || incomesChanged) {
        const hasSelfEmployment = values.hasSelfEmploymentIncome === true;
        await clientsApi.saveAssetInventoryIncome({
          clientId,
          saveAssetIncomeRequest: {
            selfEmployment: {
              hasSelfEmploymentIncome: values.hasSelfEmploymentIncome,
              description: hasSelfEmployment ? values.selfEmploymentDescription : null,
              monthlyIncome: hasSelfEmployment ? values.selfEmploymentMonthlyIncome : null,
            },
            incomes: values.incomes.map((income) => toIncomeRequest(income, traitsOf(income.incomeType))),
          },
        });
        await queryClient.invalidateQueries({ queryKey });
        void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.BankruptcyChecklist, clientId] });
        void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryExpenses, clientId] });
        void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryCompleteness, clientId] });
      }

      let uploadFailed = false;
      if (values.incomeProofs.length > 0) {
        const failed = await uploadIncomeProofs(values.incomeProofs);
        await helpers.setFieldValue(nameof<AssetIncomeFormValues>("incomeProofs"), failed);
        uploadFailed = failed.length > 0;
      }

      const failedMoves = await moveDocuments(values.documentMoves);
      await helpers.setFieldValue(nameof<AssetIncomeFormValues>("documentMoves"), failedMoves);

      if (!uploadFailed && failedMoves.length === 0) {
        showSuccess(t("clients.client.debtSettlement.assetInventory.incomeTab.saveSuccess"));
      }
    },
  });

  const { onAddMove, onCancelMove } = usePendingMovesField(formik);

  const missingFields = useMissingFieldMessages(
    clientId,
    AssetInventoryCompletenessSection.Income,
    formik.values,
    describeMissingIncomeField
  );

  return { formik, assessment: data?.assessment, missingFields, onAddMove, onCancelMove };
}
